/**
 * AuthPromptModal — inline three-view auth modal for surfaces
 * where an authenticated session is suddenly required mid-flow
 * (a share-view recipient clicking "Add to my Drive" while
 * anonymous, for instance).
 *
 * Three internal views, switchable via in-modal links — no
 * navigation away, so the recipient keeps their place:
 *
 *   - signin  → SignInForm  (email + pwd + Email-OTP + Google)
 *   - signup  → SignUpForm  (name + email + pwd + confirm + Google)
 *   - forgot  → ForgotPasswordForm  (email → "check your inbox")
 *
 * After a successful email/password sign-in, `onSuccess` fires.
 * The Google OAuth path is a full-page redirect that bypasses the
 * callback — the recipient lands back on the current URL with a
 * fresh session and can retry their original action.
 *
 * The same SignInForm / SignUpForm / ForgotPasswordForm components
 * back the standalone /login, /signup, /forgot-password pages, so
 * any behavior change to the auth flow lands in one place.
 */
import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useBrand } from '@papan/brand/react'
import { SignInForm, type SignInFormHandle } from './SignInForm'
import { SignUpForm } from './SignUpForm'
import { ForgotPasswordForm } from './ForgotPasswordForm'
import { useAuth } from '../hooks/useAuth'

type View = 'signin' | 'signup' | 'forgot'

interface Props {
  /** Action-specific title shown on the sign-in view, e.g.
   *  "Sign in to add to your Drive". Sign-up / forgot views
   *  use their own canonical titles. */
  signInTitle: string
  themeClass: string
  onClose: () => void
  /** Fires after the email/password sign-in path succeeds.
   *  Caller is responsible for closing the modal + retrying the
   *  original action. */
  onSuccess: () => void | Promise<void>
  /** Initial view. Defaults to 'signin'. */
  initialView?: View
}

// Built from the brand rather than declared as consts: two of these strings
// embed the product name, and a module-level const cannot read the snapshot.
const titleByView = (productName: string): Record<View, string> => ({
  signin: '',  // overridden by `signInTitle` prop
  signup: `Create your ${productName} account`,
  forgot: 'Reset your password',
})

const subtitleByView = (productName: string): Record<View, string> => ({
  signin: `Sign in to your ${productName} account to continue.`,
  signup: 'Quick sign-up — verify your email and you\'re in.',
  forgot: '',  // ForgotPasswordForm carries its own description.
})

export function AuthPromptModal({
  signInTitle, themeClass, onClose, onSuccess, initialView = 'signin',
}: Props) {
  const [view, setView] = useState<View>(initialView)
  const signInFormRef = useRef<SignInFormHandle>(null)
  const { productName } = useBrand()
  const { refreshSession } = useAuth()
  // #1316: re-read the session before handing back — `refreshSession` is where a changed account is announced to the
  // other tabs (a drive tab still showing another account reloads); the login page does the same.
  const signedIn = async () => { await refreshSession(); await onSuccess() }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const currentPath = window.location.pathname + window.location.search
  const title = view === 'signin' ? signInTitle : titleByView(productName)[view]
  const subtitle = subtitleByView(productName)[view]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} themed-bg-secondary themed-text border themed-border rounded-lg shadow-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto`}>
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 themed-text-muted hover:themed-text"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <h2 className="text-base font-semibold themed-text mb-1">{title}</h2>
        {subtitle && (
          <p className="text-sm themed-text-muted mb-4 leading-relaxed">{subtitle}</p>
        )}

        {view === 'signin' && (
          <SignInForm
            ref={signInFormRef}
            onSuccess={signedIn}
            googleCallbackPath={currentPath}
            onForgotPassword={() => setView('forgot')}
          />
        )}
        {view === 'signup' && (
          <SignUpForm
            googleCallbackPath={currentPath}
            // Forward current path so /verify-email returns them here
            // after the email click.
            nextParam={currentPath}
          />
        )}
        {view === 'forgot' && (
          <ForgotPasswordForm />
        )}

        <p className="text-xs themed-text-muted text-center mt-4">
          {view === 'signin' && (
            <>
              No account?{' '}
              <button
                type="button"
                onClick={() => setView('signup')}
                className="text-blue-400 hover:text-blue-300"
              >
                Sign up
              </button>
            </>
          )}
          {view === 'signup' && (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => setView('signin')}
                className="text-blue-400 hover:text-blue-300"
              >
                Sign in
              </button>
            </>
          )}
          {view === 'forgot' && (
            <button
              type="button"
              onClick={() => setView('signin')}
              className="text-blue-400 hover:text-blue-300"
            >
              ← Back to sign in
            </button>
          )}
        </p>

        {/* Dev autofill — mirrors AuthLayout's hint exactly so the
            two surfaces feel identical in local dev. Sign-in view
            only; signup/forgot have their own input shapes. */}
        {import.meta.env.DEV && view === 'signin' && (
          <p className="mt-4 text-[11px] themed-text-muted text-center opacity-60">
            Dev: kok@papan.local &middot;{' '}
            <span
              onClick={() => signInFormRef.current?.setPassword('PapanDev2026!')}
              className="cursor-pointer hover:text-blue-400 transition-colors"
            >
              PapanDev2026!
            </span>
          </p>
        )}
      </div>
    </div>
  )
}
